/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { PrintProofReviewDto2, PrintProofReviewResultEnum } from '@/api-sdk';
import { FgAutocomplete, FgDialog } from '@/components';
import { autoCompleteValidation } from '@/components/autocomplete/utils';
import { reasonRejected } from '@/data';
import { baseStyles, buttonStyles, formStyles } from '@/styles';
import { ErrorType } from '@/types';
import { handlePrintProofReview } from './utils';

import '@/components/autocomplete/fg-autocomplete';
import '@/components/fg-dialog';
import '@/components/file-upload/fg-printproof-file-upload';

@customElement('fg-print-proof-reject')
export class FgPrintProofReject extends LitElement {
  @queryAll('fg-autocomplete')
  private fgAutocomplete?: NodeListOf<FgAutocomplete>;

  private remarkTextAreas: Ref<HTMLTextAreaElement> = createRef();

  @property({ type: String })
  configurationId = '';

  @property({ type: String })
  storeCode?: string;

  @state()
  private errorMessage = '';

  @state()
  private fileUuid = '';

  @state()
  loading = false;

  private fgDialogRef: Ref<FgDialog> = createRef();

  private async validationCheck(event: SubmitEvent) {
    event.preventDefault();

    const form = event.target as HTMLFormElement,
      formData = new FormData(form);

    this.reasonValidCheck(formData);
    this.remarkValidCheck(formData);

    formData.append('uuid', this.fileUuid);

    if (!this.errorMessage && this.storeCode !== undefined) {
      const reason = formData.get('reason')?.toString(),
        remark = formData.get('remark')?.toString(),
        uuid = formData.get('uuid')?.toString() || null;

      if (reason && remark) {
        const reviewData: PrintProofReviewDto2 = {
          result: PrintProofReviewResultEnum.rejected,
          feedback: {
            reason: reason,
            remarks: remark,
            logoUuid: uuid,
          },
        };

        this.loading = true;

        try {
          await handlePrintProofReview(
            this.configurationId,
            reviewData,
            this,
            this.storeCode,
          );
          this.closeDialog();
        } catch {
          this.loading = false;
        }
      }
    }
  }

  private reasonValidCheck(formData: FormData) {
    let autoCompleteData;

    const errors: {
      [key: number]: string;
    } = {
      [ErrorType.AUTOCOMPLETE_EMPTY]: t('errors.emptyReason'),
      [ErrorType.AUTOCOMPLETE_INVALID]: t('errors.invalidReason'),
    };

    if (this.fgAutocomplete && this.fgAutocomplete.length > 0) {
      /** Swap values of reasonRejected with translated values */
      let reasonRejectCounter = 0;
      reasonRejected.map(reason => {
        reasonRejected.splice(reasonRejectCounter, 1, t(reason));
        reasonRejectCounter++;
      });

      autoCompleteData = autoCompleteValidation(
        this.fgAutocomplete,
        reasonRejected,
      );

      const { error } = autoCompleteData;

      if (error) {
        this.errorMessage = errors[error];
        return;
      }
      this.errorMessage = '';
    }

    if (!this.errorMessage && autoCompleteData) {
      const { items } = autoCompleteData;
      formData.append('reason', items[0]);
    }
  }

  private remarkValidCheck(formData: FormData) {
    const { value } = this.remarkTextAreas;

    if (!value) {
      return;
    }

    if (!value.value) {
      this.errorMessage = t('errors.emptyComment');
      return;
    }

    this.errorMessage = '';

    formData.append('remark', value.value);
  }

  handleFileUploadError(event: CustomEvent) {
    const { errorMessage } = event.detail;
    this.errorMessage = errorMessage;
  }

  handleFileUploadSuccess(event: CustomEvent) {
    const uuid = event.detail.uuid;
    this.fileUuid = uuid;
  }

  render() {
    return html`
      <fg-dialog ${ref(this.fgDialogRef)}>
        <span slot="title">${t('content.printProof.rejectPrintProof')}</span>
        <p>${t('content.printProof.rejectPrintProofText')}</p>
        <form @submit=${this.validationCheck}>
          <div class="form-section">
            <label>
              <strong>
                ${t('content.printProof.rejectPrintProofReasonText')}
              </strong>
            </label>
            <fg-autocomplete
              .items=${reasonRejected}
              .placeholder=${t('content.printProof.rejectPrintProofReason') ||
              ''}
              resetValues
              translatable
            ></fg-autocomplete>
          </div>
          <div class="form-section">
            <label for="remark">
              <strong>${t('content.printProof.remark')}</strong>
            </label>
            <textarea
              ${ref(this.remarkTextAreas)}
              id="remark"
              name="remark"
              .placeholder=${t('content.printProof.remarks') || ''}
            ></textarea>
          </div>
          <div class="form-section">
            <label for="file">
              <strong>${t('content.printProof.uploadNewFile')}</strong>
            </label>
            <fg-printproof-file-upload
              @file-upload-error=${this.handleFileUploadError}
              @file-upload-success=${this.handleFileUploadSuccess}
            ></fg-printproof-file-upload>
          </div>
          ${this.errorMessage
            ? html`
                <p class="error">${this.errorMessage}</p>
              `
            : nothing}

          <button
            class="button button-primary"
            type="submit"
            ?disabled=${this.loading}
          >
            ${t('content.printProof.rejectAndSend')}
          </button>
        </form>
      </fg-dialog>
    `;
  }

  openDialog() {
    this.fgDialogRef.value?.open();
  }

  closeDialog() {
    this.fgDialogRef.value?.close();
  }

  static styles = [
    baseStyles,
    buttonStyles,
    formStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        gap: 20px;
      }

      .form-section {
        display: flex;
        flex-direction: column;
      }

      label {
        font-size: 0.875rem;
        line-height: 1.25rem;
        margin-bottom: 2px;
      }

      textarea {
        min-height: 148px;
        margin-bottom: 0;
      }

      button.button {
        width: auto;
      }

      .error {
        width: 100%;
        text-align: center;
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
      }

      @media screen and (min-width: 768px) {
        label {
          font-size: 1rem;
          line-height: 1.375rem;
          margin-bottom: 2px;
        }
      }
    `,
  ];
}
